import React, { createContext, useState, useContext, ReactNode, useEffect, useMemo, useRef, useCallback } from 'react';
import { Restaurant, Dish, MenuCategory, SubscriptionQueueItem, Payment, User, Pack } from '../types';
import { db, storage } from '../firebase/client';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
    collection,
    query,
    getDocs,
    doc,
    getDoc,
    setDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    where,
    orderBy,
    serverTimestamp,
    collectionGroup,
    deleteField,
    increment,
    or,
    and,
    arrayUnion,
    arrayRemove
} from 'firebase/firestore';
import { wilayas as initialWilayas } from '../data/mockData';
import { useAuth } from './AuthContext';
import { formatDateReadable, checkRestaurantShouldBeOnline } from '../utils/dateUtils';
import { MOCK_RESTAURANTS_SEED, MOCK_DISHES_SEED, MOCK_PACKS_SEED, MOCK_USERS_SEED } from '../data/seedData';

interface DataContextType {
    restaurants: Restaurant[];
    wilayas: string[];
    activeWilayas: string[];
    loading: boolean;
    error: string | null;
    getRestaurantById: (id: string) => Restaurant | undefined;
    updateRestaurantRating: (restaurantId: string, newRating: number) => Promise<void>;
    updateDishRating: (restaurantId: string, dishId: number, newRating: number) => Promise<void>;
    updateRestaurantDetails: (restaurantId: string, details: Partial<Omit<Restaurant, 'id' | 'ownerId'>>) => Promise<void>;
    addDish: (restaurantId: string, dish: any) => Promise<void>;
    updateDish: (restaurantId: string, updatedDish: Dish) => Promise<void>;
    deleteDish: (restaurantId: string, dishId: number) => Promise<void>;
    addExtra: (restaurantId: string, extra: any) => Promise<void>;
    updateExtra: (restaurantId: string, extraId: string, extra: any) => Promise<void>;
    deleteExtra: (restaurantId: string, extraId: string) => Promise<void>;
    addCategory: (restaurantId: string, categoryName: string) => Promise<void>;
    deleteCategory: (restaurantId: string, categoryName: string) => Promise<void>;
    uploadImage: (file: File) => Promise<string>;
    refreshRestaurants: () => Promise<void>;
    toggleRestaurantFavorite: (restaurantId: string) => void;
    getUserRestaurantRating: (restaurantId: string) => number;
    getUserDishRating: (restaurantId: string, dishId: number) => number;
    pendingRestaurants: Restaurant[];
    allLivreurs: User[];
    allPayments: Payment[];
    validateRestaurant: (restaurantId: string, status: 'approved' | 'rejected') => Promise<void>;
    suspendRestaurant: (restaurantId: string) => Promise<void>;
    deleteRestaurant: (restaurantId: string) => Promise<void>;
    toggleRestaurantFeatured: (restaurantId: string, isFeatured: boolean) => Promise<void>;
    ensureDishesLoaded: (restaurantId: string) => Promise<void>;
    fetchCategoryDishes: (categoryName: string, restaurantIds: string[]) => Promise<void>;
    fetchPromoDishes: (restaurantIds?: string[]) => Promise<void>;
    isFetchingCategory: boolean;
    isFetchingPromos: boolean;
    totalStats: any;
    calculateRestaurantRevenue: (restaurantId: string, startDate: Date, endDate: Date, sinceDate?: Date) => Promise<number>;
    switchToCommissionPlan: (restaurantId: string) => Promise<void>;
    // Driver management
    validateLivreur: (livreurUid: string, status: 'approved' | 'rejected') => Promise<void>;
    suspendLivreur: (livreurUid: string) => Promise<void>;
    deleteLivreur: (livreurUid: string) => Promise<void>;
    // Payment management
    rejectPayment: (paymentId: string) => Promise<void>;
    validatePayment: (paymentId: string, restaurantId: string, paidAmount?: number) => Promise<void>;
    // Pack management
    addPack: (restaurantId: string, pack: Omit<Pack, 'id'>) => Promise<void>;
    updatePack: (restaurantId: string, pack: Pack) => Promise<void>;
    deletePack: (restaurantId: string, packId: string) => Promise<void>;
    ensurePacksLoaded: (restaurantId: string) => Promise<void>;
    seedLocalFirestore: () => Promise<void>;
    fetchGlobalDishesByCategory: (categoryName: string) => Promise<void>;
    globalPromos: Dish[];
    devForceShowAll: boolean;
    setDevForceShowAll: (show: boolean) => void;
    grantFreeSubscription: (restaurantId: string, planType: 'mensuel' | 'commission', days: number) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
    const [wilayas] = useState<string[]>(initialWilayas);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
    const [userRatings, setUserRatings] = useState<Record<string, number>>({});
    const [allLivreurs, setAllLivreurs] = useState<User[]>([]);
    const [allPayments, setAllPayments] = useState<Payment[]>([]);
    const isMounted = useRef(true);
    const fetchInProgressRef = useRef<boolean>(false);
    const [devForceShowAll, setDevForceShowAll] = useState(false);
    const favIdsRef = useRef<string[]>([]);
    const dishesSubscriptions = useRef<Record<string, () => void>>({});
    const packsSubscriptions = useRef<Record<string, () => void>>({});
    const [isFetchingCategory, setIsFetchingCategory] = useState(false);
    const [isFetchingPromos, setIsFetchingPromos] = useState(false);
    const [totalStats] = useState<any>(null);
    const [globalPromos] = useState<Dish[]>([]);
    const restaurantsRef = useRef<Restaurant[]>([]);

    // Sync ref for callback stability
    useEffect(() => {
        restaurantsRef.current = restaurants;
    }, [restaurants]);

    // Cleanup subscriptions on unmount
    useEffect(() => {
        return () => {
            Object.values(dishesSubscriptions.current).forEach(unsub => unsub());
            Object.values(packsSubscriptions.current).forEach(unsub => unsub());
        };
    }, []);

    // On garde le ref à jour pour les closures onSnapshot
    useEffect(() => {
        favIdsRef.current = favoriteIds;
    }, [favoriteIds]);

    // Load Favorites & Ratings from Firestore
    useEffect(() => {
        if (currentUser) {
            const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
                if (snap.exists()) {
                    setFavoriteIds(snap.data().favorites || []);
                    setUserRatings(snap.data().ratings || {});
                }
            });
            return () => unsub();
        } else {
            setFavoriteIds([]);
            setUserRatings({});
        }
    }, [currentUser]);

    const toggleRestaurantFavorite = async (restaurantId: string) => {
        if (!currentUser) return;
        const isFavorite = favoriteIds.includes(restaurantId);
        const newFavorites = isFavorite
            ? favoriteIds.filter(id => id !== restaurantId)
            : [...favoriteIds, restaurantId];

        setFavoriteIds(newFavorites);
        try {
            await setDoc(doc(db, 'users', currentUser.uid), { favorites: newFavorites }, { merge: true });
        } catch (err) {
            console.error("Error updating favorites:", err);
            setFavoriteIds(favoriteIds);
        }
    };

    const userRole = currentUser?.role || 'client';

    // SELF-HEALING: Auto-fix restaurants that have an active plan but are marked as suspended
    useEffect(() => {
        if ((userRole === 'super_admin' || userRole === 'restaurateur') && restaurants.length > 0) {
            const repairs = async () => {
                const now = new Date();
                const restaurantsToFix = restaurants.filter(r => {
                    const isMyRestaurant = r.ownerId === currentUser?.uid || (r as any).owner_id === currentUser?.uid;
                    const canRepair = userRole === 'super_admin' || isMyRestaurant;

                    return canRepair &&
                        r.status === 'suspended' && 
                        (r.subscriptionStatus === 'active' || r.subscriptionStatus === 'trial') &&
                        (r.subscriptionExpiry ? (r.subscriptionExpiry.toDate ? r.subscriptionExpiry.toDate() : new Date(r.subscriptionExpiry)) > now : true);
                });

                if (restaurantsToFix.length > 0) {
                    console.log(`[Self-Healing] Found ${restaurantsToFix.length} restaurants to reactivate...`);
                    for (const r of restaurantsToFix) {
                        try {
                            await updateDoc(doc(db, 'restaurants', r.id), { 
                                status: 'active',
                                isOnline: true,
                                updatedAt: serverTimestamp()
                            });
                            console.log(`[Self-Healing] Fixed status and isOnline for ${r.name}`);
                        } catch (err) {
                            console.error(`[Self-Healing] Error fixing ${r.name}:`, err);
                        }
                    }
                }
            };
            repairs();
        }
    }, [restaurants, userRole, currentUser?.uid]);

    const filteredRestaurantsForRole = useMemo(() => {
        const isAdmin = userRole === 'admin' || userRole === 'super_admin' || userRole === 'livreur';

        return restaurants.filter(r => {
            // Admin, Super Admin, and Deliveries see everything
            if (isAdmin) return true;

            // Owners always see their own restaurant
            if (userRole === 'restaurateur' && (r.ownerId === currentUser?.uid || (r as any).owner_id === currentUser?.uid)) return true;

            // Clients & other restaurateurs only see valid (approved) restaurants
            // mapRestaurantData handles mapping expired -> suspended, so we just check status
            return r.status === 'approved';
        });
    }, [restaurants, userRole, currentUser?.uid]);

    const activeWilayas = useMemo(() => {
        const usedWilayas = new Set(restaurants.filter(r => r.status === 'approved').map(r => r.wilaya));
        return wilayas.filter(w => usedWilayas.has(w));
    }, [restaurants, wilayas]);

    const pendingRestaurants = useMemo(() => {
        return restaurants.filter(r => r.status === 'pending');
    }, [restaurants]);

    const rawDishesToMenu = (rawDishes: any[], rid: string): MenuCategory[] => {
        const menuMap = new Map<string, Dish[]>();
        rawDishes.forEach((d: any) => {
            const dish: Dish = {
                id: d.id,
                restaurantId: rid,
                restaurant_id: rid,
                name: d.name || "Plat sans nom",
                description: d.description || "",
                price: Number(d.price) || 0,
                image: d.image,
                ingredients: d.ingredients || [],
                available: d.is_available !== false,
                is_available: d.is_available !== false,
                discount: Number(d.discount) || 0,
                rating: Number(d.rating) || 0,
                ratingCount: Number(d.ratingCount) || 0,
                is_trending: !!d.is_trending,
                categoryId: d.categoryId || d.category || 'Autres',
                category: d.category || 'Autres'
            };
            const cat = d.category || 'Autres';
            if (!menuMap.has(cat)) menuMap.set(cat, []);
            menuMap.get(cat)?.push(dish);
        });

        return Array.from(menuMap.entries()).map(([category, dishes]) => ({
            category,
            dishes
        }));
    };

    const mapRestaurantData = (id: string, r: any, dishes: any[] = [], dishesFetched = false, packs: Pack[] = []): Restaurant => {
        const safeToDate = (ts: any) => {
            if (!ts) return null;
            if (ts.toDate) return ts.toDate();
            if (ts instanceof Date) return ts;
            if (ts.seconds) return new Date(ts.seconds * 1000);
            const d = new Date(ts);
            return isNaN(d.getTime()) ? null : d;
        };

        const now = new Date();
        const tExp = safeToDate(r.trialExpiry);
        const sExp = safeToDate(r.subscriptionExpiry);
        const fExp = safeToDate(r.featuredExpiry);
        const subStatus = (r.subscriptionStatus || 'none').toLowerCase();
        const subType = (r.subscriptionType || 'none').toLowerCase();

        const queuedItems = (r.subscriptionQueue || []).map((item: any) => ({
            ...item,
            startDate: safeToDate(item.startDate),
            endDate: safeToDate(item.endDate)
        }));

        // Business Logic: Determine "Current" vs "Active"
        const isCurrentlyInTrial = (subStatus === 'trial' || subStatus === 'none') && tExp && tExp > now;
        const isSubscriptionActive = subStatus === 'active' && sExp && sExp > now;

        // Effective Display logic
        let effectiveStatus = subStatus;
        let effectiveType = subType;

        if (isSubscriptionActive) {
            effectiveStatus = 'active';
            effectiveType = subType;
        } else if (isCurrentlyInTrial) {
            effectiveStatus = 'trial';
            effectiveType = 'trial';
        } else if (sExp && sExp <= now) {
            effectiveStatus = 'expired';
        } else if (tExp && tExp <= now) {
            effectiveStatus = 'expired';
        } else if (subStatus === 'active' || subStatus === 'trial') {
            // If they are theoretically active/trial but have no valid future date, it's a data error or passed
            effectiveStatus = 'expired';
        }

        // Featured dynamic check
        const isFeaturedActive = fExp ? (fExp > now) : !!((r.featuredStatus || 'none').toLowerCase() === 'active');
        const effectiveFeaturedStatus = isFeaturedActive ? 'active' : (fExp && fExp <= now ? 'expired' : (r.featuredStatus || 'none').toLowerCase());

        return {
            id: id.trim(),
            ownerId: r.owner_id || r.ownerId,
            ownerName: r.ownerName || r.owner_name || "",
            name: r.name || "Restaurant sans nom",
            slogan: r.slogan || "",
            wilaya: r.wilaya || "Alger",
            address: r.address || "",
            phone: r.phone || r.phone_number || "",
            rating: Number(r.rating) || 0,
            ratingCount: Number(r.ratingCount || r.rating_count) || 0,
            image: r.image,
            gallery: r.gallery || [],
            description: r.description || "",
            menu: rawDishesToMenu(dishes, id.trim()),
            packs: packs, // Use provided packs instead of resetting to []
            deliveryEnabled: !!(r.delivery_enabled ?? r.deliveryEnabled),
            deliveryPrice: Number(r.delivery_price ?? r.deliveryPrice) || 0,
            globalDiscount: Number(r.global_discount ?? r.globalDiscount) || 0,
            status: (() => {
                const baseStatus = (r.status || 'approved').toLowerCase() === 'active' ? 'approved' : r.status;
                if (baseStatus === 'approved' && (effectiveStatus === 'expired')) {
                    return 'suspended';
                }
                return baseStatus;
            })() as any,
            featured: isFeaturedActive,
            isOnline: r.isOnline !== undefined ? r.isOnline : (r.is_online !== false),
            useSchedule: !!r.useSchedule,
            openingHours: r.openingHours || r.opening_hours || undefined,
            location: (() => {
                if (!r.location) return undefined;
                // Detect Firestore GeoPoint (native latitude/longitude)
                if (typeof r.location.latitude === 'number' && typeof r.location.longitude === 'number') {
                    return { lat: r.location.latitude, lng: r.location.longitude };
                }
                // Standard {lat, lng}
                if (typeof r.location.lat === 'number' && typeof r.location.lng === 'number') {
                    return { lat: r.location.lat, lng: r.location.lng };
                }
                // Fallback for numeric strings
                const lat = Number(r.location.lat ?? r.location.latitude);
                const lng = Number(r.location.lng ?? r.location.longitude);
                if (!isNaN(lat) && !isNaN(lng) && (r.location.lat !== undefined || r.location.latitude !== undefined)) {
                    return { lat, lng };
                }
                return undefined;
            })(),
            createdAt: r.createdAt?.toDate?.()?.toISOString() || r.createdAt,
            dishesFetched: dishesFetched || dishes.length > 0,
            deliveryDrivers: r.deliveryDrivers || [],
            extras: r.extras || [],
            isFavorite: favIdsRef.current.includes(id.trim()),
            // Subscription fields
            subscriptionType: effectiveType as any,
            subscriptionStatus: effectiveStatus as any,
            subscriptionExpiry: r.subscriptionExpiry,
            trialExpiry: r.trialExpiry,
            subscriptionQueue: queuedItems,
            currentPeriodExpiry: sExp || tExp, // Cumulative or Trial
            pendingSubscriptionRenewal: !!r.pendingSubscriptionRenewal,
            pendingSubscriptionType: r.pendingSubscriptionType,
            // Commission fields (FIX)
            commissionDebt: Number(r.commissionDebt || 0),
            commissionStartedAt: r.commissionStartedAt,
            // Featured fields
            featuredType: (r.featuredType || 'none').toLowerCase() as any,
            featuredStatus: effectiveFeaturedStatus as any,
            featuredExpiry: r.featuredExpiry,
            // Mandatory missing fields
            deliveryTime: r.deliveryTime || r.delivery_time || "30-45 min",
            minOrder: Number(r.minOrder || r.min_order) || 0,
            categories: r.categories || [],
            // Reservations
            tableReservationEnabled: !!(r.tableReservationEnabled ?? r.table_reservation_enabled),
            reservationSettings: r.reservationSettings || r.reservation_settings || {
                totalTables: 10,
                maxCapacityPerSlot: 40,
                slotDurationMinutes: 90,
                advanceNoticeMin: 60
            }
        };
    };



    useEffect(() => {
        if (!isMounted.current) return;
        
        // Reset loading if query changes
        setLoading(true);
        let isFirstLoad = true;

        let q = query(collection(db, 'restaurants'));
        
        // OPTIMIZATION: Clients only see restaurants in their Wilaya
        // Admins, Super Admins, and Deliveries still see everything
        const isClient = currentUser?.role === 'client';
        if (isClient && currentUser?.wilaya && !devForceShowAll) {
            console.log(`📡 DataContext: Filtering restaurants for Wilaya: ${currentUser.wilaya} (Featured fallback enabled)`);
            q = query(
                collection(db, 'restaurants'), 
                or(
                    where('wilaya', '==', currentUser.wilaya),
                    where('featuredStatus', '==', 'active'),
                    where('featured', '==', true)
                )
            );
        } else {
            console.log("📡 DataContext: No user Wilaya filter. Fetching all approved restaurants.");
        }

        const unsub = onSnapshot(q, (snap) => {
            if (!isMounted.current) return;

            const rawDocs = snap.docs.map(d => ({ id: d.id, data: d.data() }));

            setRestaurants(prev => {
                const updated = rawDocs.map(d => {
                    const existing = prev.find(r => r.id === d.id);
                    // Pass existing dishes and dishesFetched state to preserve them
                    return mapRestaurantData(
                        d.id, 
                        d.data, 
                        existing?.dishesFetched ? existing.menu.flatMap(c => c.dishes) : [], 
                        existing?.dishesFetched || false,
                        existing?.packs || []
                    );
                });
                return updated;
            });

            if (snap.empty && isClient && currentUser?.wilaya && import.meta.env.DEV) {
                console.warn(`⚠️ [DEV] No restaurants found for Wilaya "${currentUser.wilaya}". Fallback to ALL restaurants to avoid empty screen.`);
                // We don't change the listener here, but we could trigger a secondary fetch if we wanted
                // For now, let's just log it loudly.
            }

            if (isFirstLoad) {
                console.log(`✅ [DataContext] Success: Fetched ${snap.size} restaurants.`);
                setLoading(false);
                isFirstLoad = false;
            }
        }, (err) => {
            console.error("❌ Firestore Restaurants Subscription Error:", err);
            setLoading(false);
        });

        return () => unsub();
    }, [currentUser?.role, currentUser?.wilaya]);

    // Effect to check for expired subscriptions and promote queued items
    useEffect(() => {
        if (!loading && restaurants.length > 0) {
            checkAndPromoteSubscriptions(restaurants);
        }
    }, [restaurants.length, loading]); // Minimal dependencies to avoid infinite loops

    // Synchroniser isFavorite suite à l'absence de re-render sur onSnapshot
    useEffect(() => {
        if (restaurants.length === 0) return;

        setRestaurants(prev => {
            const needsUpdate = prev.some(r => r.isFavorite !== favoriteIds.includes(r.id));
            if (!needsUpdate) return prev;

            return prev.map(r => ({
                ...r,
                isFavorite: favoriteIds.includes(r.id)
            }));
        });
    }, [favoriteIds, restaurants.length]);

    const seedLocalFirestore = async () => {
        if (fetchInProgressRef.current) return;
        
        // CRITICAL CHECK: Verify absolute collection size before seeding
        // to avoid double entries if filters were just hiding existing data
        const rawSnap = await getDocs(collection(db, 'restaurants'));
        if (rawSnap.size > 0) {
            console.log(`ℹ️ [Seed] ${rawSnap.size} restaurants already exist in local Firestore. Skipping auto-seed.`);
            return;
        }

        fetchInProgressRef.current = true;
        console.log("🌱 [Seed] Database is truly empty. Starting auto-seed for local dev...");
        
        try {
            for (const restoData of MOCK_RESTAURANTS_SEED) {
                const { id, menu, packs, ...cleanData } = restoData;
                await setDoc(doc(db, 'restaurants', id), {
                    ...cleanData,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                } as any);

                // Add Dishes
                const dishes = MOCK_DISHES_SEED[id] || [];
                for (const dish of dishes) {
                    await addDoc(collection(db, 'restaurants', id, 'dishes'), {
                        ...dish,
                        createdAt: serverTimestamp()
                    });
                }

                // Add Packs
                const restoPacks = MOCK_PACKS_SEED[id] || [];
                for (const pack of restoPacks) {
                    await addDoc(collection(db, 'restaurants', id, 'packs'), {
                        ...pack,
                        createdAt: serverTimestamp()
                    });
                }
                console.log(`✅ [Seed] Populated restaurant: ${restoData.name}`);
            }

            // Add Users
            console.log("👥 [Seed] Populated mock users...");
            for (const userData of MOCK_USERS_SEED) {
                await setDoc(doc(db, 'users', userData.uid), {
                    ...userData,
                    createdAt: serverTimestamp()
                });
            }
            console.log("📦 [Seed] Seeding finished successfully.");
        } catch (err) {
            console.error("[Seed] Fatal error during seeding:", err);
        } finally {
            fetchInProgressRef.current = false;
        }
    };

    // Auto-seed if empty in DEV
    useEffect(() => {
        if (import.meta.env.DEV && !loading && restaurants.length === 0) {
            // Tiny delay to ensure snapshot has really confirmed it's empty
            const timer = setTimeout(() => {
                if (restaurants.length === 0) {
                    seedLocalFirestore();
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [loading, restaurants.length]);

    const subscribeToDishesForRestaurant = useCallback((restaurantId: string) => {
        // Eviter les doubles souscriptions
        if (dishesSubscriptions.current[restaurantId]) return;

        try {
            const q = query(collection(db, 'restaurants', restaurantId, 'dishes'));
            
            const unsub = onSnapshot(q, (snap) => {
                const dishes = snap.docs.map(d => ({ ...d.data(), id: d.id } as Dish));
                
                setRestaurants(prev => {
                    if (!prev.find(r => r.id === restaurantId)) return prev;
                    return prev.map(r =>
                        r.id === restaurantId ? { ...r, menu: rawDishesToMenu(dishes, restaurantId), dishesFetched: true } : r
                    );
                });
            }, (err) => {
                console.error(`[FIREBASE ERROR] onSnapshot plats pour ${restaurantId}:`, err.code, err.message);
                setRestaurants(prev => prev.map(r => r.id === restaurantId ? { ...r, dishesFetched: true } : r));
            });

            dishesSubscriptions.current[restaurantId] = unsub;
        } catch (err) {
            console.error("Error setting up dishes subscription:", err);
        }
    }, []);

    const subscribeToPacksForRestaurant = useCallback((restaurantId: string) => {
        if (packsSubscriptions.current[restaurantId]) return;

        try {
            const q = query(collection(db, 'restaurants', restaurantId, 'packs'));
            
            const unsub = onSnapshot(q, (snap) => {
                const packs = snap.docs.map(d => ({ ...d.data(), id: d.id } as Pack));
                
                setRestaurants(prev => {
                    if (!prev.find(r => r.id === restaurantId)) return prev;
                    return prev.map(r =>
                        r.id === restaurantId ? { ...r, packs, packsFetched: true } : r
                    );
                });
            }, (err) => {
                console.error(`[FIREBASE ERROR] onSnapshot packs pour ${restaurantId}:`, err.code, err.message);
                setRestaurants(prev => prev.map(r => r.id === restaurantId ? { ...r, packsFetched: true } : r));
            });

            packsSubscriptions.current[restaurantId] = unsub;
        } catch (err) {
            console.error("Error setting up packs subscription:", err);
        }
    }, []);

    const ensureDishesLoaded = useCallback(async (id: string) => {
        // On switch vers une souscription temps réel
        subscribeToDishesForRestaurant(id);
    }, [subscribeToDishesForRestaurant]);

    const ensurePacksLoaded = useCallback(async (id: string) => {
        subscribeToPacksForRestaurant(id);
    }, [subscribeToPacksForRestaurant]);

    // --- SUPER ADMIN LISTENERS ---
    useEffect(() => {
        if (currentUser?.role !== 'super_admin') return;

        console.log("📡 Super Admin: Subscribing to all livreurs and payments");

        // Listen to all livreurs
        const qLivreurs = query(collection(db, 'users'), where('role', '==', 'livreur'));
        const unsubLivreurs = onSnapshot(qLivreurs, (snap) => {
            const drivers = snap.docs.map(d => ({ ...d.data(), uid: d.id } as User));
            setAllLivreurs(drivers);
        });

        // Listen to all payments
        const qPayments = query(collection(db, 'payments'));
        const unsubPayments = onSnapshot(qPayments, (snap) => {
            console.log(`📡 [Super Admin] Received ${snap.docs.length} payments`);
            
            // Create a fresh array AND handle potential local snapshots with null timestamps
            const newPayments = snap.docs.map(d => ({ ...d.data(), id: d.id } as Payment));
            
            // IMMUTABLE sort to ensure React detects the change every single time
            const sortedPayments = [...newPayments].sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
                return dateB.getTime() - dateA.getTime();
            });
            
            setAllPayments(sortedPayments);
        }, (err) => {
            console.error("❌ [Super Admin] Payments listener error:", err);
        });

        return () => {
            unsubLivreurs();
            unsubPayments();
        };
    }, [currentUser?.role]);
    // -----------------------------

    const updateRestaurantDetails = async (id: string, details: any) => {
        // If updating schedule or schedule toggle, apply immediate status check
        if (details.openingHours !== undefined || details.useSchedule !== undefined) {
            const currentRest = getRestaurantById(id);
            if (currentRest) {
                const finalHours = details.openingHours !== undefined ? details.openingHours : currentRest.openingHours;
                const finalUseSchedule = details.useSchedule !== undefined ? details.useSchedule : currentRest.useSchedule;
                
                // Determine if it SHOULD be online right now
                const shouldBeOnline = checkRestaurantShouldBeOnline(finalHours, finalUseSchedule);
                
                // Force isOnline to match the schedule's requirement immediately upon update
                // We override it even if it's already in details because the form might contain stale data
                details.isOnline = shouldBeOnline;
                console.log(`🕒 Immediate status sync: ${currentRest.name} forced to ${shouldBeOnline ? 'ONLINE' : 'OFFLINE'} due to schedule update.`);
            }
        }
        await updateDoc(doc(db, 'restaurants', id.trim()), details);
    };

    const validateRestaurant = async (id: string, status: string) => {
        const rest = getRestaurantById(id);
        const updateData: any = { status };

        // If approving for the first time and they have a pending subscription
        if (status === 'approved' && rest?.subscriptionStatus === 'pending') {
            updateData.subscriptionStatus = 'active';
            // subscriptionType is already set in SubscriptionScreen for initial subs
            
            // Set initial expiry (30 days for mensuel, etc.)
            const now = new Date();
            const duration = rest.subscriptionType === 'mensuel' ? 30 : 365;
            updateData.subscriptionExpiry = new Date(now.getTime() + duration * 24 * 60 * 60 * 1000);
        }

        await updateDoc(doc(db, 'restaurants', id.trim()), updateData);
    };

    const suspendRestaurant = async (id: string) => {
        await updateDoc(doc(db, 'restaurants', id.trim()), { status: 'suspended' });
    };

    const deleteRestaurant = async (id: string) => {
        await deleteDoc(doc(db, 'restaurants', id.trim()));
    };

    const rejectPayment = async (paymentId: string) => {
        await updateDoc(doc(db, 'payments', paymentId), { status: 'rejected' });
    };

    const validatePayment = async (paymentId: string, restaurantId: string, paidAmount?: number) => {
        const now = new Date();
        
        try {
            console.log(`[Validation] Starting validation for Payment: ${paymentId}, Restaurant: ${restaurantId}`);
            
            const payment = allPayments.find(p => p.id === paymentId);
            const rest = getRestaurantById(restaurantId);
            
            if (!rest) {
                console.error(`[Validation Error] Restaurant not found: ${restaurantId}. Available IDs:`, restaurants.map(r => r.id));
                alert(`Erreur: Restaurant ${restaurantId} introuvable. Veuillez contacter le support technique.`);
                return;
            }

            if (!payment) {
                console.warn(`[Validation Warning] Payment object not found in memory for ${paymentId}. Attempting with limited data.`);
            }

            // 1. Mark this specific payment as validated
            const paymentRef = doc(db, 'payments', paymentId);
            const safePaidAmount = (paidAmount !== undefined && !isNaN(Number(paidAmount))) ? Number(paidAmount) : null;

            await updateDoc(paymentRef, { 
                status: 'validated',
                validatedAt: now,
                paidAmount: safePaidAmount !== null ? safePaidAmount : deleteField(),
            });

            // 2. Process subscription activation
            const planType = payment?.planType || rest.pendingSubscriptionType;
            if (!planType) {
                console.error("[Validation Error] No planType found for this validation request.");
                alert("Erreur: Type d'abonnement inconnu.");
                return;
            }

            const duration = planType === 'mensuel' ? 30 : (planType === 'annuel' ? 365 : 30);
            
            const safeToDate = (ts: any) => {
                if (!ts) return null;
                if (ts.toDate) return ts.toDate();
                if (ts instanceof Date) return ts;
                if (ts.seconds) return new Date(ts.seconds * 1000);
                const d = new Date(ts);
                return isNaN(d.getTime()) ? null : d;
            };

            let startDate = now;
            const currentExp = safeToDate(rest.subscriptionExpiry);
            const trialExp = safeToDate(rest.trialExpiry);
            const currentQueue = rest.subscriptionQueue || [];

            const isCommissionPlan = planType === 'commission';
            const isCurrentlyCommission = rest.subscriptionType === 'commission';

            let endDate: Date;

            if (isCurrentlyCommission || isCommissionPlan) {
                startDate = now;
                let remainingMs = 0;
                const bestCurrentExp = (currentExp && currentExp > now) ? currentExp
                    : (trialExp && trialExp > now) ? trialExp
                    : null;
                if (bestCurrentExp) {
                    remainingMs = bestCurrentExp.getTime() - now.getTime();
                }
                if (currentQueue.length > 0) {
                    const lastItem = currentQueue[currentQueue.length - 1];
                    const lastEnd = safeToDate(lastItem.endDate);
                    if (lastEnd && lastEnd > now) {
                        const queueRemainingMs = lastEnd.getTime() - now.getTime();
                        if (queueRemainingMs > remainingMs) remainingMs = queueRemainingMs;
                    }
                }
                endDate = new Date(now.getTime() + remainingMs + duration * 24 * 60 * 60 * 1000);
            } else {
                if (trialExp && trialExp > startDate) startDate = trialExp;
                if (currentExp && currentExp > startDate) startDate = currentExp;
                if (currentQueue.length > 0) {
                    const lastItem = currentQueue[currentQueue.length - 1];
                    const lastEnd = safeToDate(lastItem.endDate);
                    if (lastEnd && lastEnd > startDate) startDate = lastEnd;
                }
                endDate = new Date(startDate.getTime() + duration * 24 * 60 * 60 * 1000);
            }

            const finalExpiry = endDate;

            const newQueueItem: SubscriptionQueueItem = {
                planType: planType as 'mensuel' | 'annuel' | 'commission',
                startDate: startDate,
                endDate: finalExpiry,
                validatedAt: now
            };

            const updatedQueue = isCurrentlyCommission ? [newQueueItem] : [...currentQueue, newQueueItem];

            const updateDataToApply: any = {
                subscriptionQueue: updatedQueue,
                subscriptionExpiry: finalExpiry,
                pendingSubscriptionType: deleteField(),
                pendingSubscriptionRenewal: deleteField(),
                updatedAt: now
            };

            if (payment) {
                // If paidAmount was not specified by admin, assume FULL payment (amount in payment request)
                const actualPaid = safePaidAmount !== null ? safePaidAmount : Number(payment.amount || 0);
                const expectedAmount = Number(payment.amount || 0);
                const diff = expectedAmount - actualPaid;
                
                console.log(`[Validation Math] ${payment.restaurantName}: Expected ${expectedAmount} - Paid ${actualPaid} = Diff ${diff}`);

                if (planType === 'commission') {
                    updateDataToApply.commissionStartedAt = now;
                    if (!isNaN(diff)) {
                        // Fix accuracy and ensure no negative debt
                        const finalDebt = Math.max(0, diff);
                        updateDataToApply.commissionDebt = Number(finalDebt < 1 ? 0 : finalDebt); 
                        console.log(`[Validation Math] Target commissionDebt: ${updateDataToApply.commissionDebt}`);
                    }
                } else if ((planType === 'mensuel' || planType === 'annuel') && isCurrentlyCommission) {
                    if (diff <= 0) {
                        updateDataToApply.commissionDebt = 0;
                        updateDataToApply.commissionStartedAt = deleteField();
                    } else if (!isNaN(diff)) {
                        updateDataToApply.commissionDebt = Number(diff);
                    }
                }
            }

            if (isCurrentlyCommission || !currentExp || currentExp <= now) {
                updateDataToApply.subscriptionType = planType;
                updateDataToApply.subscriptionStatus = 'active';
                // Reset suspension and restore visibility to clients
                updateDataToApply.status = 'active';
                updateDataToApply.isOnline = true;
            }

            // Execute updates
            console.log(`[Validation] Finalizing updates for ${rest.name}...`);
            
            await updateDoc(paymentRef, {
                startDate: startDate,
                endDate: endDate,
                finalizedAt: serverTimestamp()
            });

            await updateDoc(doc(db, 'restaurants', restaurantId), updateDataToApply);
            
            console.log(`[Validation Success] Payment ${paymentId} validated successfully.`);
            alert("Paiement validé avec succès !");

        } catch (err: any) {
            console.error(`[Validation Fatal Error] Failed to validate payment ${paymentId}:`, err);
            alert(`Erreur technique lors de la validation: ${err.message || 'Inconnue'}`);
        }
    };

    const validateLivreur = async (uid: string, status: 'approved' | 'rejected') => {
        await updateDoc(doc(db, 'users', uid), { livreurStatus: status });
    };

    const suspendLivreur = async (uid: string) => {
        await updateDoc(doc(db, 'users', uid), { livreurStatus: 'suspended' });
    };

    const deleteLivreur = async (uid: string) => {
        // Technically we might want to just disable them, but delete as requested
        await deleteDoc(doc(db, 'users', uid));
    };

    const switchToCommissionPlan = async (restaurantId: string) => {
        const rest = getRestaurantById(restaurantId);
        if (!rest) return;

        console.log(`[Subscription] Requesting commission switch for ${rest.name} (${restaurantId})`);

        // Set pending flags on restaurant
        await updateDoc(doc(db, 'restaurants', restaurantId), {
            pendingSubscriptionType: 'commission',
            pendingSubscriptionRenewal: false,
            updatedAt: serverTimestamp()
        });

        // Create a formal payment record so the Super Admin sees the request
        await addDoc(collection(db, 'payments'), {
            restaurantId: restaurantId,
            restaurantName: rest.name,
            type: 'subscription',
            category: 'commission_activation',
            plan: 'commission',
            planType: 'commission',
            period: 'Commission',
            amount: 0, 
            proofUrl: 'auto_commission',
            status: 'pending',
            timestamp: serverTimestamp(),
            createdAt: serverTimestamp(),
            description: "Demande de passage au modèle par Commission"
        });
    };

    const calculateRestaurantRevenue = async (restaurantId: string, startDate: Date, endDate: Date, sinceDate?: Date): Promise<number> => {
        try {
            console.log(`[REVENUE] Calculating for ${restaurantId} from ${startDate.toISOString()} to ${endDate.toISOString()} (sinceDate: ${sinceDate?.toISOString()})`);
            const ordersRef = collection(db, 'orders');
            
            // Use 'and' and 'or' to combine filters accurately.
            const q = query(
                ordersRef,
                and(
                    where('status', 'in', ['Livrée', 'Terminée']),
                    or(
                        where('restaurantId', '==', restaurantId),
                        where('id_restaurant', '==', restaurantId),
                        where('restaurant_id', '==', restaurantId)
                    )
                )
            );
            
            const snap = await getDocs(q);
            let total = 0;
            
            const actualStart = (sinceDate && sinceDate > startDate) ? sinceDate : startDate;
            console.log(`[REVENUE] Found ${snap.docs.length} completed orders for restaurant. filtering by date >= ${actualStart.toISOString()}`);

            snap.docs.forEach(d => {
                const data = d.data();
                
                const createdAtRaw = data.createdAt || data.timestamp;
                const createdAt = createdAtRaw?.toDate ? createdAtRaw.toDate() : (createdAtRaw ? new Date(createdAtRaw) : null);
                
                if (createdAt && createdAt >= actualStart && createdAt <= endDate) {
                    const price = Number(data.totalPrice || data.total_price || 0);
                    // BEEP: We subtract delivery fee if it's included in totalPrice, but usually 
                    // the commission is on the net revenue (dishes only). 
                    // Let's assume user wants 5% of (totalPrice - deliveryFee).
                    const deliveryFee = Number(data.deliveryFee || data.delivery_fee || 0);
                    total += (price - deliveryFee);
                }
            });

            console.log(`[REVENUE] Final total for ${restaurantId}: ${total}`);
            return total;
        } catch (err) {
            console.error("Error calculating revenue for restaurant", restaurantId, ":", err);
            return 0;
        }
    };

    /**
     * Checks if any restaurant in the list needs a subscription promotion
     * (e.g. Trial ended, move to the first queued plan)
     */
    const checkAndPromoteSubscriptions = async (restaurantList: Restaurant[]) => {
        const now = new Date();
        
        for (const rest of restaurantList) {
            if (!rest.subscriptionQueue || rest.subscriptionQueue.length === 0) continue;

            const currentType = rest.subscriptionType;
            const trialExp = rest.trialExpiry?.toDate ? rest.trialExpiry.toDate() : (rest.trialExpiry ? new Date(rest.trialExpiry) : null);
            const currentExp = rest.subscriptionExpiry?.toDate ? rest.subscriptionExpiry.toDate() : (rest.subscriptionExpiry ? new Date(rest.subscriptionExpiry) : null);

            // Determine if the current period has ended
            let periodEnded = false;
            if (rest.subscriptionStatus === 'trial' && trialExp && now > trialExp) {
                periodEnded = true;
            } else if (rest.subscriptionStatus === 'active' && currentExp && now > currentExp) {
                // This shouldn't normally happen if we have a queue, because subscriptionExpiry 
                // is the UV (Ultimate Validity) date.
                // But we check if the SPECIFIC current plan in the queue is done.
            }

            // More robust: If subscriptionType matches something but it's not the top of the queue 
            // OR if we are in trial but trial is over.
            const firstQueueItem = rest.subscriptionQueue[0];
            const itemStartDate = firstQueueItem.startDate?.toDate ? firstQueueItem.startDate.toDate() : new Date(firstQueueItem.startDate);
            
            if (now >= itemStartDate && rest.subscriptionType !== firstQueueItem.planType) {
                console.log(`[SubscriptionPromotion] Promoting ${rest.name} to ${firstQueueItem.planType}`);
                
                await updateDoc(doc(db, 'restaurants', rest.id), {
                    subscriptionType: firstQueueItem.planType,
                    subscriptionStatus: 'active',
                    // We keep the item in the queue for history, or remove it?
                    // Let's remove processed items to keep it clean.
                    subscriptionQueue: rest.subscriptionQueue.slice(1)
                });
            }
        }
    };

    const toggleRestaurantFeatured = async (id: string, isFeatured: boolean) => {
        const now = new Date();
        const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        await updateDoc(doc(db, 'restaurants', id.trim()), { 
            featuredStatus: isFeatured ? 'active' : 'none',
            featuredType: isFeatured ? '1_week' : 'none',
            featuredExpiry: isFeatured ? sevenDaysLater : null,
            featured: isFeatured, // Keep for backward compatibility if needed
            is_featured: isFeatured // Keep for backward compatibility if needed
        });
    };

    const grantFreeSubscription = async (restaurantId: string, planType: 'mensuel' | 'commission', days: number) => {
        const rest = getRestaurantById(restaurantId);
        if (!rest) return;

        const now = new Date();
        const durationMs = days * 24 * 60 * 60 * 1000;
        
        let newExpiry: Date;
        const currentExp = rest.subscriptionExpiry?.toDate ? rest.subscriptionExpiry.toDate() : (rest.subscriptionExpiry ? new Date(rest.subscriptionExpiry) : null);
        
        if (currentExp && currentExp > now) {
            newExpiry = new Date(currentExp.getTime() + durationMs);
        } else {
            newExpiry = new Date(now.getTime() + durationMs);
        }

        const updateData: any = {
            subscriptionExpiry: newExpiry,
            subscriptionType: planType,
            subscriptionStatus: 'active',
            status: 'approved',
            isOnline: true,
            updatedAt: serverTimestamp()
        };

        if (planType === 'commission' && !rest.commissionStartedAt) {
            updateData.commissionStartedAt = serverTimestamp();
        }

        await updateDoc(doc(db, 'restaurants', restaurantId.trim()), updateData);
        
        // Also add a formal record in history for traceability
        await addDoc(collection(db, 'payments'), {
            restaurantId: restaurantId,
            restaurantName: rest.name,
            type: 'subscription',
            category: 'free_grant',
            plan: planType,
            planType: planType,
            period: `${days} jours (Offert)`,
            amount: 0,
            proofUrl: 'super_admin_grant',
            status: 'validated',
            timestamp: serverTimestamp(),
            createdAt: serverTimestamp(),
            validatedAt: serverTimestamp(),
            description: `Abonnement offert par Super Admin (${days} jours)`
        });
    };

    const addDish = async (restaurantId: string, dish: any) => {
        const { id, category, ...dataToSave } = dish;
        await setDoc(doc(collection(db, 'restaurants', restaurantId.trim(), 'dishes')), {
            ...dataToSave,
            category: category || 'General',
            createdAt: serverTimestamp()
        });
    };

    const updateDish = async (restaurantId: string, updatedDish: Dish) => {
        const { id, ...data } = updatedDish;
        await updateDoc(doc(db, 'restaurants', restaurantId.trim(), 'dishes', String(id).trim()), data);
    };

    const deleteDish = async (restaurantId: string, dishId: number) => {
        await deleteDoc(doc(db, 'restaurants', restaurantId.trim(), 'dishes', String(dishId).trim()));
    };

    const addExtra = async (restaurantId: string, extra: any) => {
        await updateDoc(doc(db, 'restaurants', restaurantId.trim()), {
            extras: arrayUnion({ ...extra, id: Date.now().toString() })
        });
    };

    const updateExtra = async (restaurantId: string, extraId: string, extraData: any) => {
        const rest = getRestaurantById(restaurantId);
        if (!rest) return;
        const newExtras = (rest.extras || []).map(e => e.id === extraId ? { ...extraData, id: extraId } : e);
        await updateDoc(doc(db, 'restaurants', restaurantId.trim()), { extras: newExtras });
    };

    const deleteExtra = async (restaurantId: string, extraId: string) => {
        const rest = getRestaurantById(restaurantId);
        if (!rest) return;
        const newExtras = (rest.extras || []).filter(e => e.id !== extraId);
        await updateDoc(doc(db, 'restaurants', restaurantId.trim()), { extras: newExtras });
    };

    const addPack = async (restaurantId: string, pack: Omit<Pack, 'id'>) => {
        await addDoc(collection(db, 'restaurants', restaurantId.trim(), 'packs'), {
            ...pack,
            createdAt: serverTimestamp()
        });
    };

    const updatePack = async (restaurantId: string, pack: Pack) => {
        const { id, ...data } = pack;
        await updateDoc(doc(db, 'restaurants', restaurantId.trim(), 'packs', id), data);
    };

    const deletePack = async (restaurantId: string, packId: string) => {
        await deleteDoc(doc(db, 'restaurants', restaurantId.trim(), 'packs', packId));
    };

    const compressImage = async (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    const MAX_DIMENSION = 1000;

                    if (width > height) {
                        if (width > MAX_DIMENSION) {
                            height *= MAX_DIMENSION / width;
                            width = MAX_DIMENSION;
                        }
                    } else {
                        if (height > MAX_DIMENSION) {
                            width *= MAX_DIMENSION / height;
                            height = MAX_DIMENSION;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const newFile = new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now(),
                            });
                            resolve(newFile);
                        } else {
                            resolve(file);
                        }
                    }, 'image/jpeg', 0.8);
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const uploadImage = async (file: File): Promise<string> => {
        if (!file) throw new Error("Aucun fichier fourni");

        try {
            // Compress image before upload
            const compressedFile = await compressImage(file);

            // Generate a unique filename
            const filename = `${Date.now()}_${compressedFile.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
            const storageRef = ref(storage, `images/${filename}`);

            const snapshot = await uploadBytes(storageRef, compressedFile);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error("Erreur lors de l'upload de l'image:", error);
            throw error;
        }
    };

    const getRestaurantById = (id: string) => restaurants.find(r => r.id === id);

    const mergeDishesIntoRestaurant = useCallback((restaurantId: string, newDishes: Dish[]) => {
        setRestaurants(prev => {
            const resto = prev.find(r => r.id === restaurantId);
            if (!resto) return prev;
            
            const currentDishesFlat: Dish[] = [];
            if (resto.menu) {
                resto.menu.forEach(cat => {
                    if (cat.dishes) currentDishesFlat.push(...cat.dishes);
                    // Also check for 'items' just in case some old data structure exists, 
                    // but 'dishes' is the primary one from types.ts
                    else if ((cat as any).items) currentDishesFlat.push(...(cat as any).items);
                });
            }

            const dishesMap = new Map<string, Dish>();
            currentDishesFlat.forEach(d => dishesMap.set(d.id, d));
            newDishes.forEach(d => dishesMap.set(d.id, d));
            
            const allDishes = Array.from(dishesMap.values());
            
            return prev.map(r => 
                r.id === restaurantId 
                    ? { ...r, menu: rawDishesToMenu(allDishes, restaurantId) } 
                    : r
            );
        });
    }, []);

    const fetchCategoryDishes = useCallback(async (categoryName: string, _restaurantIds?: string[]) => {
        setIsFetchingCategory(true);
        console.log(`[DataContext] Discovery attempt for: ${categoryName}`);
        
        try {
            let foundGlobally = false;
            // Strategy 1: Global collectionGroup (Fastest, requires index)
            try {
                const qGlobal = query(collectionGroup(db, 'dishes'), where('category', '==', categoryName));
                const snapGlobal = await getDocs(qGlobal);
                
                if (!snapGlobal.empty) {
                    foundGlobally = true;
                    const byResto = new Map<string, Dish[]>();
                    snapGlobal.docs.forEach(doc => {
                        const rid = doc.ref.parent.parent?.id;
                        if (rid) {
                            if (!byResto.has(rid)) byResto.set(rid, []);
                            byResto.get(rid)?.push({ ...doc.data(), id: doc.id } as Dish);
                        }
                    });
                    
                    await Promise.all(Array.from(byResto.entries()).map(async ([rid, dishes]) => {
                        const existing = restaurantsRef.current.find(r => r.id === rid);
                        if (existing) {
                            mergeDishesIntoRestaurant(rid, dishes);
                        } else {
                            const rSnap = await getDoc(doc(db, 'restaurants', rid));
                            if (rSnap.exists()) {
                                setRestaurants(prev => {
                                    if (prev.find(r => r.id === rid)) return prev;
                                    return [...prev, mapRestaurantData(rid, rSnap.data(), dishes, false)];
                                });
                            }
                        }
                    }));
                }
            } catch (globalErr: any) {
                if (globalErr.code === 'failed-precondition' || globalErr.message.toLowerCase().includes('index')) {
                    console.warn("[DataContext] Global Category Index Missing. Link to create:", globalErr.message);
                }
            }

            // Strategy 2: Manual Fallback (Slower but reliable)
            if (!foundGlobally) {
                console.log("[DataContext] Global search yielded nothing or failed. Falling back to manual discovery...");
                // Focus on restaurants in the current state (usually filtered by Wilaya already)
                const targets = restaurantsRef.current.filter(r => r.status === 'approved');
                if (targets.length > 0) {
                    await Promise.all(targets.map(async (resto) => {
                        try {
                            const q = query(collection(db, 'restaurants', resto.id, 'dishes'), where('category', '==', categoryName));
                            const s = await getDocs(q);
                            if (!s.empty) {
                                mergeDishesIntoRestaurant(resto.id, s.docs.map(d => ({ ...d.data(), id: d.id } as Dish)));
                            }
                        } catch (e) {}
                    }));
                }
            }
        } catch (err: any) {
            console.error(`[DataContext] Category discovery error:`, err.message);
        } finally {
            setIsFetchingCategory(false);
        }
    }, [mergeDishesIntoRestaurant]);

    const fetchPromoDishes = useCallback(async (restaurantIds?: string[]) => {
        setIsFetchingPromos(true);
        try {
            // Priority 1: Global collectionGroup query (Best coverage)
            try {
                const qGlobal = query(collectionGroup(db, 'dishes'), where('discount', '>', 0));
                const snapGlobal = await getDocs(qGlobal);
                
                if (!snapGlobal.empty) {
                    const byResto = new Map<string, Dish[]>();
                    snapGlobal.docs.forEach(doc => {
                        const data = doc.data();
                        const rid = doc.ref.parent.parent?.id;
                        if (rid) {
                            if (!byResto.has(rid)) byResto.set(rid, []);
                            byResto.get(rid)?.push({ ...data, id: doc.id } as Dish);
                        }
                    });
                    
                    // For each restaurant found, ensure we have the profile and merge dishes
                    await Promise.all(Array.from(byResto.entries()).map(async ([rid, dishes]) => {
                        const existingResto = restaurantsRef.current.find(r => r.id === rid);
                        if (!existingResto) {
                            try {
                                const rSnap = await getDoc(doc(db, 'restaurants', rid));
                                if (rSnap.exists()) {
                                    const rData = mapRestaurantData(rid, rSnap.data(), dishes, true);
                                    setRestaurants(prev => {
                                        if (prev.find(pr => pr.id === rid)) return prev;
                                        return [...prev, rData];
                                    });
                                }
                            } catch (e) { }
                        } else {
                            mergeDishesIntoRestaurant(rid, dishes);
                        }
                    }));
                    console.log(`[DataContext] Global promos loaded: ${snapGlobal.size} dishes found.`);
                }
            } catch (globalErr: any) {
                if (globalErr.code === 'failed-precondition') {
                    console.warn("[DataContext] Global promo index missing. Link to create it:", globalErr.message);
                }
            }

            // Priority 2: Targeted fetch for restaurants with globalDiscount (Fallback)
            const targets = restaurantIds || restaurantsRef.current.filter(r => r.status === 'approved' && (r.globalDiscount || 0) > 0).map(r => r.id);
            
            if (targets.length > 0) {
                await Promise.all(targets.map(async (id) => {
                    try {
                        const q = query(collection(db, 'restaurants', id, 'dishes'), where('discount', '>', 0));
                        const snap = await getDocs(q);
                        const dishes = snap.docs.map(doc => ({ ...doc.data(), id: doc.id } as Dish));
                        if (dishes.length > 0) {
                            mergeDishesIntoRestaurant(id, dishes);
                        }
                    } catch (err) { }
                }));
            }
        } finally {
            setIsFetchingPromos(false);
        }
    }, [mergeDishesIntoRestaurant]);

    const refreshRestaurants = async () => {
        // onSnapshot gère déjà le temps réel, mais on peut forcer un re-fetch si besoin ici
    };

    const updateRestaurantRating = async (restaurantId: string, newRating: number) => {
        if (!currentUser) return;
        const rest = getRestaurantById(restaurantId);
        if (!rest) return;

        const previousUserRating = userRatings[restaurantId] || 0;
        const isNewRating = previousUserRating === 0;

        let newTotalCount = isNewRating ? rest.ratingCount + 1 : rest.ratingCount;
        let newTotalScore = (rest.rating * rest.ratingCount) - previousUserRating + newRating;
        let finalRating = newTotalCount > 0 ? newTotalScore / newTotalCount : 0;

        try {
            const updatedRatings = { ...userRatings, [restaurantId]: newRating };
            setUserRatings(updatedRatings);
            
            // Optimistic UI Update: Update the local restaurant object immediately
            setRestaurants(prev => prev.map(r => {
                if (r.id === restaurantId) {
                    return {
                        ...r,
                        rating: finalRating,
                        ratingCount: newTotalCount
                    };
                }
                return r;
            }));

            console.log(`⭐ Optimistic update: New rating ${finalRating.toFixed(1)} (${newTotalCount} avis)`);

            // 1. Personal rating history
            await setDoc(doc(db, 'users', currentUser.uid), { ratings: updatedRatings }, { merge: true });
            
            // 2. Submit for global aggregation (securely handled by Cloud Function)
            // Use a deterministic ID to ensure one rating per user per restaurant
            const ratingId = `${currentUser.uid}_${restaurantId}`;
            const ratingRef = doc(db, 'restaurant_ratings', ratingId);
            
            await setDoc(ratingRef, {
                restaurantId,
                rating: newRating,
                userId: currentUser.uid,
                userName: currentUser.name || 'Client',
                timestamp: serverTimestamp()
            });

            console.log(`✅ Rating ${newRating} saved to Firestore [doc: ${ratingId}]`);
        } catch (err) {
            console.error("❌ Error updating rating:", err);
            // Optional: Revert optimistic update here if needed
        }
    };
    const updateDishRating = async () => { }; // Placeholder
    const getUserRestaurantRating = (restaurantId: string) => userRatings[restaurantId] || 0;
    const getUserDishRating = () => 0;
    const addCategory = async () => { };
    const deleteCategory = async () => { };
    const fetchGlobalDishesByCategory = async () => { };

    return (
        <DataContext.Provider value={{
            restaurants: filteredRestaurantsForRole,
            wilayas,
            activeWilayas,
            loading,
            error,
            getRestaurantById,
            updateRestaurantRating,
            updateDishRating,
            updateRestaurantDetails,
            addDish,
            updateDish,
            deleteDish,
            addExtra,
            updateExtra,
            deleteExtra,
            addCategory,
            deleteCategory,
            uploadImage,
            refreshRestaurants,
            toggleRestaurantFavorite,
            getUserRestaurantRating,
            getUserDishRating,
            pendingRestaurants,
            allLivreurs,
            allPayments,
            validateRestaurant,
            suspendRestaurant,
            deleteRestaurant,
            validatePayment,
            toggleRestaurantFeatured,
            ensureDishesLoaded,
            fetchCategoryDishes,
            fetchPromoDishes,
            isFetchingCategory,
            isFetchingPromos,
            totalStats,
            calculateRestaurantRevenue,
            switchToCommissionPlan,
            validateLivreur,
            suspendLivreur,
            deleteLivreur,
            rejectPayment,
            addPack,
            updatePack,
            deletePack,
            ensurePacksLoaded,
            seedLocalFirestore,
            devForceShowAll,
            setDevForceShowAll,
            grantFreeSubscription
        }}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => {
    const context = useContext(DataContext);
    if (context === undefined) throw new Error('useData must be used within a DataProvider');
    return context;
};